import { useState, useRef, useEffect } from 'react';
import { ChevronRight, ChevronDown, GraduationCap, BookOpen, MapPin, Building2, Briefcase, Brain, Target, Check, Route, Info, FileText, TrendingUp, CheckCircle, Zap, Lock } from 'lucide-react';
import { usePortfolio } from '@contexts/PortfolioContext';
import { useReports } from '@contexts/ReportsContext';
import { usePlanRestrictions } from '@hooks/usePlanRestrictions';
import PlanUpgradeModal from '@components/common/PlanUpgradeModal';

const PROFILE_OPTIONS = [
  { value: 'student', label: 'Student', tooltip: 'Exploring academic courses, exams, and career options.' },
  { value: 'fresher', label: 'Fresher', tooltip: 'Pursuing graduation, building skills, and preparing for jobs and placements.' },
  { value: 'professional', label: 'Working Professional', tooltip: 'Working or experienced, focused on career change and growth.' },
];

const CareerRoadmap = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // Tooltip is rendered via fixed positioning so it can never be clipped by the
  // scrollable dropdown list (an absolutely-positioned tooltip inside an
  // overflow-y-auto container gets cut off — this sidesteps that entirely).
  const [tooltip, setTooltip] = useState(null); // { value, top, left, width }
  // Dropdown list is rendered with fixed positioning (see dropdownPos) so it can
  // never be clipped by a scrollable ancestor (e.g. the page's main content area
  // when the button sits near the bottom of a short viewport).
  const [dropdownPos, setDropdownPos] = useState(null); // { top, left, width, maxHeight, direction }
  const dropdownRef = useRef(null); // wraps the trigger button
  const dropdownPanelRef = useRef(null); // wraps the fixed panel itself
  const buttonRef = useRef(null);
  const { activeReport } = useReports();
  const { canAccessProfileType } = usePlanRestrictions();
  const isProfileTypeLocked = activeReport?.isProfileTypeLocked || false;

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inButton = dropdownRef.current && dropdownRef.current.contains(event.target);
      const inPanel = dropdownPanelRef.current && dropdownPanelRef.current.contains(event.target);
      if (!inButton && !inPanel) {
        setIsDropdownOpen(false);
        setDropdownPos(null);
        setTooltip(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close tooltip on scroll/resize so it never floats in a stale position
  useEffect(() => {
    if (!tooltip) return;
    const clear = () => setTooltip(null);
    window.addEventListener('scroll', clear, true);
    window.addEventListener('resize', clear);
    return () => {
      window.removeEventListener('scroll', clear, true);
      window.removeEventListener('resize', clear);
    };
  }, [tooltip]);

  // Close the dropdown itself on scroll/resize — its position was computed
  // against the trigger button's rect at open-time, so it goes stale otherwise.
  useEffect(() => {
    if (!isDropdownOpen) return;
    const clear = () => {
      setIsDropdownOpen(false);
      setDropdownPos(null);
      setTooltip(null);
    };
    window.addEventListener('scroll', clear, true);
    window.addEventListener('resize', clear);
    return () => {
      window.removeEventListener('scroll', clear, true);
      window.removeEventListener('resize', clear);
    };
  }, [isDropdownOpen]);

  const computeDropdownPosition = (buttonEl) => {
    const rect = buttonEl.getBoundingClientRect();
    const margin = 8;
    const gap = 4;
    const desiredMaxHeight = 260;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;

    const openDown = spaceBelow >= 120 || spaceBelow >= spaceAbove;

    if (openDown) {
      return {
        direction: 'down',
        top: rect.bottom + gap,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.max(100, Math.min(desiredMaxHeight, spaceBelow)),
      };
    }
    return {
      direction: 'up',
      bottom: window.innerHeight - rect.top + gap,
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(100, Math.min(desiredMaxHeight, spaceAbove)),
    };
  };

  const computeTooltipPosition = (targetEl) => {
    const rect = targetEl.getBoundingClientRect();
    const margin = 8;
    const gap = 6;
    const desiredWidth = Math.min(256, window.innerWidth - margin * 2); // w-64 cap, shrinks on tiny screens
    let left = rect.right - desiredWidth;
    left = Math.max(margin, Math.min(left, window.innerWidth - desiredWidth - margin));

    // Estimate tooltip height (two lines of small text + padding) to decide
    // whether it fits below the icon or needs to flip above it — otherwise,
    // for rows near the bottom of the viewport (e.g. the last dropdown item),
    // the tooltip renders off-screen and is invisible, as happened here.
    const estimatedHeight = 64;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const openBelow = spaceBelow >= estimatedHeight || spaceBelow >= spaceAbove;

    if (openBelow) {
      return { direction: 'down', top: rect.bottom + gap, left, width: desiredWidth };
    }
    return { direction: 'up', bottom: window.innerHeight - rect.top + gap, left, width: desiredWidth };
  };

  const showTooltip = (e, opt) => {
    const pos = computeTooltipPosition(e.currentTarget);
    setTooltip({ value: opt.value, ...pos });
  };

  const hideTooltip = () => setTooltip(null);

  const toggleTooltipTouch = (e, opt) => {
    e.stopPropagation();
    if (tooltip?.value === opt.value) {
      hideTooltip();
    } else {
      const pos = computeTooltipPosition(e.currentTarget);
      setTooltip({ value: opt.value, ...pos });
    }
  };

  const {
    careerStage,
    selectedCourse,
    selectedSubjects,
    selectedState,
    selectedUniversities,
    opportunityType,
    selectedPersonalityTraits,
    selectedCareerDomains,
    selectedAptitudeTopics,
    selectedExams,
    selectedJobs,
    selectedIndustryOutlook,
    resumeReady,
    resumeFile,
    courses,
    universities,
    personalityTraits,
    careerDomains,
    updateCareerStage,
    // Fresher-specific
    pursuingCourse,
    courseDuration,
    campusName,
    fresherAddress,
    fresherState,
    fresherDistrict,
    cgpaRange,
    // Professional-specific
    professionalTier,
    industryFocus,
    experienceLevel,
    careerSwitchPreferences,
    professionalSoftSkills,
    selectedHardSkills,
  } = usePortfolio();

  const getCompletedSteps = () => {
    let completed = 0;
    if (careerStage) completed++;

    if (careerStage === 'fresher') {
      if (pursuingCourse && courseDuration && campusName) completed++;
      if (fresherAddress && fresherState && fresherDistrict) completed++;
      if (cgpaRange) completed++;
      if (selectedAptitudeTopics.length > 0) completed++;
      if (selectedExams.length > 0) completed++;
      if (selectedJobs.length > 0) completed++;
      if (selectedIndustryOutlook.length > 0) completed++;
      if (resumeReady) completed++;
    } else if (careerStage === 'professional') {
      if (professionalTier) completed++;
      if (industryFocus) completed++;
      if (professionalTier === 'Career Switch' && careerSwitchPreferences.length > 0) completed++;
      if (experienceLevel) completed++;
      if (professionalSoftSkills.length > 0) completed++;
      if (selectedHardSkills.length > 0) completed++;
      if (selectedJobs.length > 0) completed++;
      if (resumeReady) completed++;
    } else {
      if (selectedCourse) completed++;
      if (selectedSubjects.length > 0) completed++;
      if (selectedState) completed++;
      if (selectedUniversities.length > 0) completed++;
      if (opportunityType) completed++;
      if (selectedPersonalityTraits.length > 0) completed++;
      if (selectedCareerDomains.length > 0) completed++;
      if (selectedExams.length > 0) completed++;
      if (selectedJobs.length > 0) completed++;
    }

    return completed;
  };

  const totalSteps = careerStage === 'fresher' ? 9 : careerStage === 'professional' ? (professionalTier === 'Career Switch' ? 9 : 8) : 9;
  const completedSteps = getCompletedSteps();
  const progressPercentage = Math.round((completedSteps / totalSteps) * 100);

  const getCourseName = () => {
    if (!selectedCourse) return 'Not selected yet';
    const course = courses.find(c => c.id === selectedCourse);
    return course ? course.name : 'Not selected yet';
  };

  const getSubjectsDisplay = () => {
    if (selectedSubjects.length === 0) return 'Not selected yet';
    return `${selectedSubjects.length} subject${selectedSubjects.length > 1 ? 's' : ''} selected`;
  };

  const getStateName = () => {
    return selectedState || 'Not selected yet';
  };

  const getUniversitiesDisplay = () => {
    if (selectedUniversities.length === 0) return 'Not selected yet';
    const universityNames = selectedUniversities.map(uniId => {
      const uni = universities.find(u => u.id === uniId);
      return uni ? uni.universityName : '';
    }).filter(Boolean);
    return universityNames.join(', ') || 'Not selected yet';
  };

  const getOpportunityDisplay = () => {
    if (!opportunityType) return 'Not selected yet';
    return opportunityType === 'private' ? 'Private' : 'Government';
  };

  const getPersonalityTraitsDisplay = () => {
    if (selectedPersonalityTraits.length === 0) return 'Not selected yet';
    return `${selectedPersonalityTraits.length} trait${selectedPersonalityTraits.length > 1 ? 's' : ''} selected`;
  };

  const getCareerDomainsDisplay = () => {
    if (selectedCareerDomains.length === 0) return 'Not selected yet';
    const domainNames = selectedCareerDomains.map(domainId => {
      const domain = careerDomains.find(d => d.id === domainId);
      return domain ? domain.name : '';
    }).filter(Boolean);
    return domainNames.join(', ') || 'Not selected yet';
  };

  const getFresherRoadmapItems = () => [
    { id: 1, icon: GraduationCap, label: 'Career Stage', value: 'Fresher', status: 'active', editable: false },
    {
      id: 2,
      icon: GraduationCap,
      label: 'Education',
      value: pursuingCourse && courseDuration && campusName
        ? `${pursuingCourse} (${courseDuration} years) - ${campusName}`
        : 'Not selected yet',
      status: pursuingCourse && courseDuration && campusName ? 'active' : 'pending',
      editable: false,
    },
    {
      id: 3,
      icon: MapPin,
      label: 'Location',
      value: fresherAddress && fresherState && fresherDistrict
        ? `${fresherDistrict}, ${fresherState}`
        : 'Not selected yet',
      status: fresherAddress && fresherState && fresherDistrict ? 'active' : 'pending',
      editable: false,
    },
    { id: 4, icon: Target, label: 'CGPA', value: cgpaRange || 'Not selected yet', status: cgpaRange ? 'active' : 'pending', editable: false },
    {
      id: 5,
      icon: BookOpen,
      label: 'Aptitude Topics',
      value: selectedAptitudeTopics.length > 0
        ? `${selectedAptitudeTopics.length} topic${selectedAptitudeTopics.length > 1 ? 's' : ''} selected`
        : 'Not selected yet',
      status: selectedAptitudeTopics.length > 0 ? 'active' : 'pending',
      editable: false,
    },
    {
      id: 6,
      icon: FileText,
      label: 'Exams',
      value: selectedExams.length > 0 ? selectedExams.map(e => e.examName).join(', ') : 'Not selected yet',
      status: selectedExams.length > 0 ? 'active' : 'pending',
      editable: false,
    },
    {
      id: 7,
      icon: Briefcase,
      label: 'Job Profiles',
      value: selectedJobs.length > 0 ? selectedJobs.map(j => j.title).join(', ') : 'Not selected yet',
      status: selectedJobs.length > 0 ? 'active' : 'pending',
      editable: false,
    },
    {
      id: 8,
      icon: TrendingUp,
      label: 'Industry Outlook',
      value: selectedIndustryOutlook.length > 0
        ? `${selectedIndustryOutlook.length} industr${selectedIndustryOutlook.length > 1 ? 'ies' : 'y'} selected`
        : 'Not selected yet',
      status: selectedIndustryOutlook.length > 0 ? 'active' : 'pending',
      editable: false,
    },
    {
      id: 9,
      icon: CheckCircle,
      label: 'Resume Ready',
      value: resumeReady ? (resumeReady === 'yes' ? (resumeFile ? `Yes (${resumeFile.name})` : 'Yes - Upload pending') : 'No') : 'Not selected yet',
      status: resumeReady ? 'active' : 'pending',
      editable: false,
    },
  ];

  const getStudentRoadmapItems = () => [
    {
      id: 1,
      icon: GraduationCap,
      label: 'Career Stage',
      value: careerStage ? careerStage.charAt(0).toUpperCase() + careerStage.slice(1) : 'Not selected yet',
      status: careerStage ? 'active' : 'pending',
      editable: false,
    },
    { id: 2, icon: GraduationCap, label: 'Course', value: getCourseName(), status: selectedCourse ? 'active' : 'pending', editable: false },
    { id: 3, icon: BookOpen, label: 'Subject', value: getSubjectsDisplay(), status: selectedSubjects.length > 0 ? 'active' : 'pending', editable: false },
    { id: 4, icon: MapPin, label: 'State', value: getStateName(), status: selectedState ? 'active' : 'pending', editable: false },
    { id: 5, icon: Building2, label: 'Institution', value: getUniversitiesDisplay(), status: selectedUniversities.length > 0 ? 'active' : 'pending', editable: false },
    {
      id: 6,
      icon: Briefcase,
      label: 'Opportunity Type',
      value: getOpportunityDisplay(),
      status: opportunityType ? 'active' : 'pending',
      editable: false,
      badge: opportunityType === 'private' ? 'Private' : opportunityType === 'govt' ? 'Government' : null,
    },
    { id: 7, icon: Brain, label: 'Learner Traits', value: getPersonalityTraitsDisplay(), status: selectedPersonalityTraits.length > 0 ? 'active' : 'pending', editable: false },
    { id: 8, icon: Target, label: 'Academic Disciplines', value: getCareerDomainsDisplay(), status: selectedCareerDomains.length > 0 ? 'active' : 'pending', editable: false },
    {
      id: 9,
      icon: FileText,
      label: 'Exams',
      value: selectedExams.length > 0 ? selectedExams.map(e => e.examName).join(', ') : 'Not selected yet',
      status: selectedExams.length > 0 ? 'active' : 'pending',
      editable: false,
    },
  ];

  const getProfessionalRoadmapItems = () => {
    const items = [
      { id: 1, icon: GraduationCap, label: 'Career Stage', value: 'Working Professional', status: 'active', editable: false },
      { id: 2, icon: Briefcase, label: 'Professional Tier', value: professionalTier || 'Not selected yet', status: professionalTier ? 'active' : 'pending', editable: false },
      { id: 3, icon: Building2, label: 'Industry Focus', value: industryFocus || 'Not selected yet', status: industryFocus ? 'active' : 'pending', editable: false },
    ];

    if (professionalTier === 'Career Switch') {
      items.push({
        id: 4,
        icon: TrendingUp,
        label: 'Career Switch Preferences',
        value: careerSwitchPreferences.length > 0
          ? `${careerSwitchPreferences.length} preference${careerSwitchPreferences.length > 1 ? 's' : ''} selected`
          : 'Not selected yet',
        status: careerSwitchPreferences.length > 0 ? 'active' : 'pending',
        editable: false,
      });
    }

    items.push(
      {
        id: professionalTier === 'Career Switch' ? 5 : 4,
        icon: Target,
        label: 'Experience Level',
        value: experienceLevel || 'Not selected yet',
        status: experienceLevel ? 'active' : 'pending',
        editable: false,
      },
      {
        id: professionalTier === 'Career Switch' ? 6 : 5,
        icon: Brain,
        label: 'Soft Skills',
        value: professionalSoftSkills.length > 0
          ? `${professionalSoftSkills.length} skill${professionalSoftSkills.length > 1 ? 's' : ''} selected`
          : 'Not selected yet',
        status: professionalSoftSkills.length > 0 ? 'active' : 'pending',
        editable: false,
      },
      {
        id: professionalTier === 'Career Switch' ? 7 : 6,
        icon: Zap,
        label: 'Hard Skills',
        value: selectedHardSkills.length > 0
          ? `${selectedHardSkills.length} skill${selectedHardSkills.length > 1 ? 's' : ''} selected`
          : 'Not selected yet',
        status: selectedHardSkills.length > 0 ? 'active' : 'pending',
        editable: false,
      },
      {
        id: professionalTier === 'Career Switch' ? 8 : 7,
        icon: Briefcase,
        label: 'Job Profiles',
        value: selectedJobs.length > 0 ? selectedJobs.map(j => j.role || j.title).join(', ') : 'Not selected yet',
        status: selectedJobs.length > 0 ? 'active' : 'pending',
        editable: false,
      }
    );

    return items;
  };

  const roadmapItems = careerStage === 'fresher' ? getFresherRoadmapItems() : careerStage === 'professional' ? getProfessionalRoadmapItems() : getStudentRoadmapItems();

  const activeTooltipOpt = tooltip ? PROFILE_OPTIONS.find(o => o.value === tooltip.value) : null;

  return (
    <div className="space-y-3 sm:space-y-4 max-w-full">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 max-w-full overflow-hidden">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-[#fee2e2] rounded-lg flex-shrink-0">
            <Route className="w-4 h-4 sm:w-5 sm:h-5 text-[#c33c3d]" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 leading-tight truncate">
              Your Career Roadmap
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 truncate">
              See your progress across all career steps
            </p>
          </div>
        </div>
      </div>

      {/* Profile Type - Highlighted Separate Card */}
      <div className="bg-gradient-to-br from-secondary-50 to-secondary-100/50 rounded-xl border border-secondary-200 shadow-md p-3 sm:p-4 w-full mb-3 sm:mb-4 relative z-10">
        <div className="w-full">
          <div className="flex items-center gap-2 sm:gap-3 mb-0.5">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-secondary-500 flex items-center justify-center flex-shrink-0">
              <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-secondary-700">
              Profile Type
            </h3>
          </div>
          <p className="text-xs sm:text-sm text-secondary-600 mb-2 sm:mb-3 ml-10 sm:ml-[52px]">
            {isProfileTypeLocked ? (
              <span className="flex items-center gap-1.5 text-amber-600">
                <Lock className="w-3 h-3" />
                Profile type locked. Generate new report to change.
              </span>
            ) : (
              'Select your current career stage'
            )}
          </p>
          <div className="relative" ref={dropdownRef}>
            <button
              ref={buttonRef}
              onClick={() => {
                if (isProfileTypeLocked) return;
                if (isDropdownOpen) {
                  setIsDropdownOpen(false);
                  setDropdownPos(null);
                } else {
                  setDropdownPos(computeDropdownPosition(buttonRef.current));
                  setIsDropdownOpen(true);
                }
                setTooltip(null);
              }}
              disabled={isProfileTypeLocked}
              className={`w-full px-2.5 sm:px-3 py-2 sm:py-2.5 bg-white border rounded-lg text-left focus:outline-none transition-all flex items-center justify-between text-xs sm:text-sm shadow-sm ${
                isProfileTypeLocked
                  ? 'border-gray-300 bg-gray-50 cursor-not-allowed opacity-75'
                  : careerStage
                    ? 'border-secondary-500 bg-white'
                    : 'border-secondary-300 hover:border-secondary-400'
              }`}
            >
              <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
                {isProfileTypeLocked ? (
                  <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600 flex-shrink-0" />
                ) : careerStage ? (
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full bg-secondary-500 flex items-center justify-center flex-shrink-0">
                    <Check className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-white" />
                  </div>
                ) : null}
                <span className={`truncate ${careerStage ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                  {careerStage ? careerStage.charAt(0).toUpperCase() + careerStage.slice(1) : 'Select career stage'}
                </span>
              </div>
              {!isProfileTypeLocked && (
                <ChevronDown className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-secondary-500 transition-transform flex-shrink-0 ml-2 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              )}
            </button>

          </div>
        </div>
      </div>

      {/* Fixed-position dropdown panel — escapes ANY scrollable/limited-height
          ancestor (the button's relative parent no longer constrains it), and
          auto-flips above the button when there isn't enough room below. */}
      {isDropdownOpen && !isProfileTypeLocked && dropdownPos && (
        <div
          ref={dropdownPanelRef}
          className="fixed z-50 bg-white border border-secondary-200 rounded-lg shadow-lg overflow-y-auto overscroll-contain"
          style={{
            left: dropdownPos.left,
            width: dropdownPos.width,
            maxHeight: dropdownPos.maxHeight,
            ...(dropdownPos.direction === 'down' ? { top: dropdownPos.top } : { bottom: dropdownPos.bottom }),
          }}
        >
          {PROFILE_OPTIONS.map((opt) => {
            const hasAccess = canAccessProfileType(opt.value);
            return (
              <div
                key={opt.value}
                onClick={() => {
                  if (!hasAccess) {
                    setShowUpgradeModal(true);
                    setIsDropdownOpen(false);
                    setDropdownPos(null);
                    setTooltip(null);
                    return;
                  }
                  updateCareerStage(opt.value);
                  setIsDropdownOpen(false);
                  setDropdownPos(null);
                  setTooltip(null);
                }}
                className={`px-2.5 sm:px-3 py-2 sm:py-2.5 transition-colors text-xs sm:text-sm flex items-center justify-between gap-2 ${
                  !hasAccess
                    ? 'cursor-not-allowed opacity-60 bg-gray-50'
                    : careerStage === opt.value
                      ? 'bg-secondary-50 text-secondary-600 font-medium cursor-pointer'
                      : 'text-gray-700 hover:bg-secondary-50/50 cursor-pointer'
                }`}
              >
                <div className="flex items-center gap-2">
                  {!hasAccess && <Lock className="w-3.5 h-3.5 text-red-500" />}
                  <span>{opt.label}</span>
                </div>
                {/* Info icon: hover on desktop, tap-to-toggle on touch — tooltip itself
                    is rendered separately via fixed positioning (see below) so it can
                    never get clipped by this scrollable list. */}
                <span
                  className="relative flex-shrink-0 cursor-pointer p-0.5"
                  onMouseEnter={(e) => showTooltip(e, opt)}
                  onMouseLeave={hideTooltip}
                  onClick={(e) => toggleTooltipTouch(e, opt)}
                >
                  <Info className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 hover:text-secondary-500" />
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Fixed-position tooltip — lives outside the scrollable dropdown in the
          stacking sense, so overflow-y-auto on the list above can never clip it.
          Recalculated on every show; hidden on scroll/resize. */}
      {tooltip && activeTooltipOpt && (
        <div
          className="fixed z-[999] px-3 py-2 rounded-md bg-white border border-gray-200 text-gray-700 text-[11px] sm:text-xs leading-snug shadow-lg pointer-events-none"
          style={{
            left: tooltip.left,
            width: tooltip.width,
            ...(tooltip.direction === 'up' ? { bottom: tooltip.bottom } : { top: tooltip.top }),
          }}
        >
          {canAccessProfileType(activeTooltipOpt.value) ? activeTooltipOpt.tooltip : 'Upgrade your plan to access this profile type'}
        </div>
      )}

      {/* Career Journey Progress - Main White Card */}
      {careerStage && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 max-w-full overflow-hidden">
          <div className="mb-3 sm:mb-4 w-full">
            <div className="flex items-center justify-between mb-1 sm:mb-1.5">
              <span className="text-[10px] sm:text-xs font-medium text-gray-700">
                Your Career Journey
              </span>
              <span className="text-[10px] sm:text-xs font-semibold text-secondary-600">
                {progressPercentage}% Complete
              </span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <span className="text-[10px] sm:text-xs text-secondary-600 font-semibold flex-shrink-0">
                {completedSteps}
              </span>
              <span className="text-[10px] sm:text-xs text-gray-400 flex-shrink-0">/{totalSteps} steps</span>
              <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden min-w-0">
                <div
                  className="h-full bg-secondary-500 transition-all duration-500"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          </div>

          <div className="space-y-1.5 sm:space-y-2 w-full">
            {roadmapItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.status === 'active';

              return (
                <div
                  key={item.id}
                  className={`relative rounded-lg border transition-all ${
                    isActive ? 'bg-secondary-50 border-secondary-200' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between p-2 sm:p-3 rounded-lg">
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      <div
                        className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isActive ? 'bg-secondary-500 text-white' : 'bg-gray-200 text-gray-400'
                        }`}
                      >
                        {isActive ? (
                          <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-white flex items-center justify-center">
                            <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-secondary-500" />
                          </div>
                        ) : (
                          <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 border-gray-400" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5">
                          <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-600 flex-shrink-0" />
                          <span className="text-[10px] sm:text-xs font-semibold text-gray-900 truncate">
                            {item.label}
                          </span>
                        </div>
                        <p className={`text-[10px] sm:text-xs ${isActive ? 'text-gray-600' : 'text-gray-400 italic'}`}>
                          {item.value}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Plan Upgrade Modal */}
      <PlanUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </div>
  );
};

export default CareerRoadmap;